import { useEffect, useMemo, useState } from 'react'

import { SourceChunk } from 'apps/aiAutomation/components/Playground/types'
import { KnowledgeEntry } from 'shared/api/requests/aiBusinessKnowledge/schemas'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { useAccountReleaseToggle } from 'utils/services/featureFlagsService/releaseTogglesHooks'

interface UseSourcesProps {
  highlightedChunks?: SourceChunk[]
  entryId?: string | null
  selectedEntry?: KnowledgeEntry | null
}

export const useSources = ({ highlightedChunks = [], entryId, selectedEntry }: UseSourcesProps) => {
  const isHighlightingEnabled = useAccountReleaseToggle(
    AccountFlag.ENABLE_AI_PLAYGROUND_SOURCE_HIGHLIGHTING,
  )

  const entryText = selectedEntry?.data.text || ''

  const processedHighlights = useMemo(() => {
    if (!entryText || highlightedChunks.length === 0 || !isHighlightingEnabled) {
      return []
    }

    const items = highlightedChunks
      .map((chunk) => {
        const startIndex = entryText.indexOf(chunk.text)
        return {
          ...chunk,
          startIndex,
          endIndex: startIndex + chunk.text.length,
        }
      })
      .filter((item) => item.startIndex !== -1)
      .sort((a, b) => a.startIndex - b.startIndex)

    if (items.length === 0) {
      return []
    }

    const merged: typeof items = []
    for (const current of items) {
      const last = merged.at(-1)

      if (last && current.startIndex <= last.endIndex) {
        if (current.endIndex > last.endIndex) {
          const overlapOffset = last.endIndex - current.startIndex
          const newPart = current.text.slice(overlapOffset)
          last.text += newPart
          last.endIndex = current.endIndex
        }
      } else {
        merged.push(current)
      }
    }

    const totalHighlightedLength = merged.reduce((sum, chunk) => sum + chunk.text.length, 0)
    const threshold = entryText.length * 0.5

    if (totalHighlightedLength > threshold) {
      return []
    }

    return merged.map((chunk, index) => ({
      ...chunk,
      id: index.toString(),
    }))
  }, [entryText, highlightedChunks, isHighlightingEnabled])

  const [selectedChunkId, setSelectedChunkId] = useState<string | null>(null)

  useEffect(() => setSelectedChunkId(null), [entryId])

  return {
    highlightedChunks: processedHighlights,
    selectedChunkId,
    setSelectedChunkId,
  }
}
